import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import documentEditorService from '../../services/documentEditor.service';
import { useNavigate } from 'react-router-dom';

const SUPPORTED_TRANSLATION_LANGUAGES = [
  'Spanish',
  'French',
  'German',
  'Italian',
  'Portuguese',
  'Japanese',
  'Chinese (Simplified)',
  'Chinese (Traditional)',
  'Korean',
];

const TranslationsPage = ({ projectId }) => {
  const navigate = useNavigate();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState({});
  
  // Add Language Dialog state
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);
  const [countryEntries, setCountryEntries] = useState([]);
  const [siteEntries, setSiteEntries] = useState([]);
  const [selectedCountryForLanguage, setSelectedCountryForLanguage] = useState('');
  const [selectedSiteForLanguage, setSelectedSiteForLanguage] = useState('');
  const [availableSitesForLanguage, setAvailableSitesForLanguage] = useState([]);
  const [newLanguage, setNewLanguage] = useState('');
  const [customLanguageText, setCustomLanguageText] = useState('');
  const [selectedVersionForLanguage, setSelectedVersionForLanguage] = useState('');
  const [availableVersionsForLanguage, setAvailableVersionsForLanguage] = useState([]);
  const [languageError, setLanguageError] = useState('');
  const [languagesLoading, setLanguagesLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      refreshLanguages();
      refreshCountryEntries();
    }
  }, [projectId]);

  const refreshCountryEntries = async () => {
    if (!projectId) return;
    try {
      const entries = await documentEditorService.getICFCountrySummaries(projectId);
      setCountryEntries(Array.isArray(entries) ? entries : []);
    } catch (err) {
      console.error('Error fetching country entries:', err);
    }
  };

  const refreshSiteEntries = async (country) => {
    if (!projectId || !country) {
      setSiteEntries([]);
      setAvailableSitesForLanguage([]);
      return;
    }
    try {
      const entries = await documentEditorService.getICFSiteSummaries(projectId, { country });
      const entriesArray = Array.isArray(entries) ? entries : [];
      setSiteEntries(entriesArray);
      
      // Update available sites for the selected country
      const sitesForCountry = entriesArray.filter(
        entry => entry.country === country
      );
      setAvailableSitesForLanguage(sitesForCountry);
    } catch (err) {
      console.error('Error fetching site entries:', err);
      setSiteEntries([]);
      setAvailableSitesForLanguage([]);
    }
  };

  const refreshLanguages = async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await documentEditorService.getICFLanguages(projectId);
      setLanguages(data || []);
    } catch (err) {
      console.error('Error fetching languages:', err);
      setError(err?.message || 'Failed to load languages.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (languageId) => {
    if (!projectId) return;
    setApproving(prev => ({ ...prev, [languageId]: true }));
    try {
      await documentEditorService.approveICFLanguage(languageId, projectId);
      await refreshLanguages();
    } catch (err) {
      console.error('Error approving language:', err);
      alert(err?.message || 'Failed to approve translation.');
    } finally {
      setApproving(prev => ({ ...prev, [languageId]: false }));
    }
  };

  const handleDelete = async (languageId) => {
    if (!window.confirm('Are you sure you want to delete this translation?')) {
      return;
    }
    if (!projectId) return;
    try {
      await documentEditorService.deleteICFLanguage(languageId, projectId);
      await refreshLanguages();
    } catch (err) {
      console.error('Error deleting language:', err);
      alert(err?.message || 'Failed to delete translation.');
    }
  };

  const handleViewTranslation = (languageEntry) => {
    if (!projectId) {
      alert('Project is still loading. Please try again in a moment.');
      return;
    }
    if (!languageEntry?.country || !languageEntry?.site || !languageEntry?.language) {
      alert('This translation entry is missing country, site, or language.');
      return;
    }

    const encodedCountry = encodeURIComponent(languageEntry.country);
    const encodedSite = encodeURIComponent(languageEntry.site);
    const encodedLanguage = encodeURIComponent(languageEntry.language);

    let url = `/document-editor/study-form?project=${projectId}&tab=icf&country=${encodedCountry}&site=${encodedSite}&language=${encodedLanguage}`;
    if (languageEntry.version) {
      url += `&version=${encodeURIComponent(languageEntry.version)}`;
    }

    // Navigate to ICF editor with forReview parameter only if NOT approved
    // If approved, use fromApproved parameter to show read-only translated content only
    if (languageEntry.approved) {
      url += '&fromApproved=true';
    } else {
      url += '&forReview=true';
    }
    navigate(url);
  };

  // Add Language Dialog functions
  const openLanguageDialog = () => {
    setSelectedCountryForLanguage('');
    setSelectedSiteForLanguage('');
    setAvailableSitesForLanguage([]);
    setSiteEntries([]);
    setNewLanguage('');
    setCustomLanguageText('');
    setSelectedVersionForLanguage('');
    setAvailableVersionsForLanguage([]);
    setLanguageError('');
    setIsLanguageDialogOpen(true);
  };

  const closeLanguageDialog = () => {
    setIsLanguageDialogOpen(false);
    setSelectedCountryForLanguage('');
    setSelectedSiteForLanguage('');
    setAvailableSitesForLanguage([]);
    setSiteEntries([]);
    setNewLanguage('');
    setCustomLanguageText('');
    setSelectedVersionForLanguage('');
    setAvailableVersionsForLanguage([]);
    setLanguageError('');
  };

  const handleCountrySelectForLanguage = async (country) => {
    setSelectedCountryForLanguage(country);
    setSelectedSiteForLanguage('');
    setSelectedVersionForLanguage('');
    setAvailableVersionsForLanguage([]);
    
    // Fetch sites for the selected country (this will update availableSitesForLanguage)
    await refreshSiteEntries(country);
  };

  const handleSiteSelectForLanguage = async (siteName) => {
    setSelectedSiteForLanguage(siteName);
    setSelectedVersionForLanguage('');
    setAvailableVersionsForLanguage([]);
    
    // Find the site entry
    const siteEntry = siteEntries.find(
      entry => entry.site === siteName && entry.country === selectedCountryForLanguage
    );
    
    if (siteEntry && projectId && selectedCountryForLanguage) {
      try {
        // Find the selected country entry
        const countryEntry = countryEntries.find(
          entry => entry.name === selectedCountryForLanguage || entry.country === selectedCountryForLanguage
        );
        
        const entryVersions = [];
        
        // 1. Selected country version
        if (countryEntry && 
            (countryEntry.name === selectedCountryForLanguage || countryEntry.country === selectedCountryForLanguage) &&
            countryEntry.latestVersionLabel) {
          entryVersions.push(countryEntry.latestVersionLabel);
        }
        
        // 2. Selected site version
        if (siteEntry && 
            siteEntry.site === siteName && 
            siteEntry.country === selectedCountryForLanguage &&
            siteEntry.latestVersionLabel) {
          if (siteEntry.country === selectedCountryForLanguage) {
            entryVersions.push(siteEntry.latestVersionLabel);
          }
        }
        
        // Remove duplicates and sort
        const uniqueVersions = [...new Set(entryVersions)];
        setAvailableVersionsForLanguage(uniqueVersions);
      } catch (err) {
        console.error('Error fetching versions:', err);
      }
    }
  };

  const handleLanguageSubmit = async () => {
    if (!selectedCountryForLanguage) {
      setLanguageError('Please select a country.');
      return;
    }
    if (!selectedSiteForLanguage) {
      setLanguageError('Please select a site.');
      return;
    }
    if (!newLanguage) {
      setLanguageError('Please select a language.');
      return;
    }
    if (newLanguage === 'Custom' && !customLanguageText.trim()) {
      setLanguageError('Please enter a custom language name.');
      return;
    }
    if (!selectedVersionForLanguage) {
      setLanguageError('Please select a version.');
      return;
    }
    if (!projectId) {
      setLanguageError('Project ID is missing.');
      return;
    }
    
    const finalLanguage = newLanguage === 'Custom' ? customLanguageText.trim() : newLanguage.trim();

    try {
      setLanguagesLoading(true);
      setLanguageError('');
      
      await documentEditorService.createICFLanguage(projectId, {
        country: selectedCountryForLanguage,
        site: selectedSiteForLanguage,
        language: finalLanguage,
        version: selectedVersionForLanguage,
      });
      
      // Refresh languages list
      await refreshLanguages();
      closeLanguageDialog();
    } catch (err) {
      console.error('Error creating language:', err);
      setLanguageError(err?.message || 'Failed to create language. Please try again.');
    } finally {
      setLanguagesLoading(false);
    }
  };

  return (
    <Box sx={{ py: 3, px: 2, minHeight: '60vh' }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            ICF Translations
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openLanguageDialog}
            disabled={loading}
            sx={{ 
              background: 'linear-gradient(135deg, #0b74b5 0%, #16a085 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0a5a8a 0%, #128a73 100%)',
              }
            }}
          >
            Add Language
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage and approve ICF language translations. Only approved translations will appear in the ICF plan.
        </Typography>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Language</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 200 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && languages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : languages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                    No translations available. Add languages in the ICF Editor.
                  </TableCell>
                </TableRow>
              ) : (
                languages.map((langEntry) => (
                  <TableRow key={langEntry._id} hover>
                    <TableCell>{langEntry.country || '—'}</TableCell>
                    <TableCell>{langEntry.site || '—'}</TableCell>
                    <TableCell>{langEntry.language}</TableCell>
                    <TableCell>{langEntry.version}</TableCell>
                    <TableCell>
                      {langEntry.approved ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Approved"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="Pending"
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant={langEntry.approved ? "outlined" : "contained"}
                          onClick={() => handleViewTranslation(langEntry)}
                          startIcon={<VisibilityIcon />}
                        >
                          {langEntry.approved ? 'View' : 'Review & Approve'}
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(langEntry._id)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {error && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 2, color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Language Dialog */}
        <Dialog open={isLanguageDialogOpen} onClose={closeLanguageDialog} fullWidth maxWidth="sm">
          <DialogTitle>Add Language Translation</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Select country, site, language, and version to create a translation entry. The ICF will be translated to the selected language when you open it.
            </Typography>
            
            {/* Step 1: Select Country */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="country-select-label">Country *</InputLabel>
              <Select
                labelId="country-select-label"
                label="Country *"
                value={selectedCountryForLanguage}
                onChange={(event) => {
                  handleCountrySelectForLanguage(event.target.value);
                  if (languageError) setLanguageError('');
                }}
              >
                {countryEntries.map((entry) => (
                  <MenuItem key={entry.countryKey || entry.name} value={entry.name}>
                    {entry.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Step 2: Select Site */}
            <FormControl fullWidth sx={{ mb: 2 }} disabled={!selectedCountryForLanguage}>
              <InputLabel id="site-select-label">Site *</InputLabel>
              <Select
                labelId="site-select-label"
                label="Site *"
                value={selectedSiteForLanguage}
                onChange={(event) => {
                  handleSiteSelectForLanguage(event.target.value);
                  if (languageError) setLanguageError('');
                }}
              >
                {availableSitesForLanguage.map((entry) => (
                  <MenuItem key={`${entry.country}-${entry.site}`} value={entry.site}>
                    {entry.site}
                  </MenuItem>
                ))}
              </Select>
              {!selectedCountryForLanguage && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Please select a country first
                </Typography>
              )}
            </FormControl>

            {/* Step 3: Select Language */}
            <FormControl fullWidth sx={{ mb: 2 }} disabled={!selectedSiteForLanguage}>
              <InputLabel id="language-select-label">Language *</InputLabel>
              <Select
                labelId="language-select-label"
                label="Language *"
                value={newLanguage}
                onChange={(event) => {
                  setNewLanguage(event.target.value);
                  if (languageError) setLanguageError('');
                }}
              >
                {SUPPORTED_TRANSLATION_LANGUAGES.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang}
                  </MenuItem>
                ))}
                <MenuItem value="Custom">Custom…</MenuItem>
              </Select>
              {!selectedSiteForLanguage && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Please select a site first
                </Typography>
              )}
            </FormControl>
            
            {newLanguage === 'Custom' && (
              <TextField
                fullWidth
                label="Custom Language"
                placeholder="Enter language name"
                value={customLanguageText}
                onChange={(event) => {
                  setCustomLanguageText(event.target.value);
                  if (languageError) setLanguageError('');
                }}
                sx={{ mb: 2 }}
                helperText="Enter the language name (e.g., Hindi, Arabic, Russian)"
              />
            )}

            {/* Step 4: Select Version */}
            <FormControl fullWidth disabled={!selectedSiteForLanguage}>
              <InputLabel id="version-select-label">Version *</InputLabel>
              <Select
                labelId="version-select-label"
                label="Version *"
                value={selectedVersionForLanguage}
                onChange={(event) => {
                  setSelectedVersionForLanguage(event.target.value);
                  if (languageError) setLanguageError('');
                }}
              >
                {availableVersionsForLanguage.map((version) => (
                  <MenuItem key={version} value={version}>
                    {version}
                  </MenuItem>
                ))}
              </Select>
              {!selectedSiteForLanguage && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Please select a site first
                </Typography>
              )}
            </FormControl>
            
            {languageError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {languageError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLanguageDialog} disabled={languagesLoading}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleLanguageSubmit}
              disabled={
                languagesLoading || 
                !selectedCountryForLanguage || 
                !selectedSiteForLanguage || 
                !newLanguage || 
                (newLanguage === 'Custom' && !customLanguageText.trim()) ||
                !selectedVersionForLanguage
              }
            >
              {languagesLoading ? <CircularProgress size={20} /> : 'Add Language'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default TranslationsPage;

